import { KeyRound, Pencil, Trash2 } from "lucide-react";
import { AssignmentFileLink } from "@/components/AssignmentFileLink";
import { ParentSubmissionReview } from "@/components/ParentSubmissionReview";
import { SubmissionStatusBadge } from "@/components/SubmissionStatusBadge";

export const hasGrade = (value) => value !== null && value !== undefined && String(value).trim() !== "";

export const AssignmentDetailsBody = ({ assignment, submission, onChanged, startEditingGrade = false }) => (
  <div className="space-y-3 text-sm" data-testid={`assignment-details-body-${assignment.id}`}>
    {assignment.description && <div className="text-slate-700"><b>Instructions:</b> {assignment.description}</div>}
    {assignment.video_url && <a data-testid={`assignment-video-${assignment.id}`} href={assignment.video_url} target="_blank" rel="noreferrer" className="text-teal-700 font-semibold">Open video link ↗</a>}
    {assignment.attachments?.length > 0 && (
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Student-visible attachments</div>
        <div className="flex flex-wrap gap-3">
          {assignment.attachments.map((file, index) => <AssignmentFileLink key={file.file_id || index} file={file} testId={`assignment-attachment-${assignment.id}-${index}`} />)}
        </div>
      </div>
    )}

    {(assignment.answer_key_text || assignment.answer_key_file) && (
      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid={`assignment-answer-key-${assignment.id}`}>
        <div className="flex items-center gap-2 text-xs text-amber-900 uppercase tracking-widest font-semibold"><KeyRound size={13} /> Parent Answer Key</div>
        {assignment.answer_key_text && <div data-testid={`assignment-answer-key-text-${assignment.id}`} className="mt-2 whitespace-pre-wrap text-slate-800">{assignment.answer_key_text}</div>}
        {assignment.answer_key_file && <div className="mt-2"><AssignmentFileLink file={assignment.answer_key_file} testId={`assignment-answer-key-file-${assignment.id}`} /></div>}
      </div>
    )}

    <ParentSubmissionReview assignment={assignment} submission={submission} onChanged={onChanged} startEditingGrade={startEditingGrade} />
  </div>
);

export const ParentAssignmentCard = ({ assignment, submission, onEdit, onDelete, onChanged, defaultOpen = false }) => (
  <div className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`ps-assignment-${assignment.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-semibold text-slate-900" data-testid={`assignment-title-${assignment.id}`}>{assignment.title}</div>
          {assignment.archived && <span className="chip" data-testid={`assignment-archived-${assignment.id}`}>Archived</span>}
          {hasGrade(assignment.grade) && <span className="chip">{assignment.grade}</span>}
          <SubmissionStatusBadge status={submission?.status || assignment.status} testId={`assignment-submission-status-${assignment.id}`} />
        </div>
        <div className="text-xs text-slate-500">{assignment.subject} · due {assignment.due_date || "—"} · <span className="uppercase tracking-widest">{assignment.status}</span></div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button type="button" data-testid={`assignment-edit-${assignment.id}`} onClick={()=>onEdit(assignment.id)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:border-teal-300 hover:text-teal-800">
          <Pencil size={14} /> Edit
        </button>
        <button type="button" data-testid={`assignment-delete-${assignment.id}`} onClick={()=>onDelete(assignment.id)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-200 text-sm font-semibold text-red-700 hover:bg-red-50">
          <Trash2 size={14} /> Delete
        </button>
      </div>
    </div>

    <details className="mt-3" open={defaultOpen || undefined}>
      <summary data-testid={`assignment-details-toggle-${assignment.id}`} className="text-sm font-semibold text-teal-700 cursor-pointer">View assignment details</summary>
      <div className="mt-3"><AssignmentDetailsBody assignment={assignment} submission={submission} onChanged={onChanged} /></div>
    </details>
  </div>
);
